import { useState, useEffect, useRef } from 'react';
import { getCompanyDirectorData } from '../../api/company.api';
import {
  CompanyDirector,
  CompanyDirectorResponse,
  UseCompanyDirectorResult,
} from '../../types/companydirector.type';
import {
  DirectorSortBy,
  DirectorOrderBy,
  WidgetMessageType,
} from '../../utils/type';
import { WidgetError, WidgetMessage } from '../../types/common.type';

const useCompanyDirector = (
  ric: string = '',
  token: string = '',
  sortBy: DirectorSortBy = DirectorSortBy.Title,
  orderBy: DirectorOrderBy = DirectorOrderBy.Asc,
  onError?: (error: WidgetError) => void,
  onNoData?: (message: WidgetMessage) => void,
): UseCompanyDirectorResult => {
  const [data, setData] = useState<CompanyDirector[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const onErrorRef = useRef(onError);
  const onNoDataRef = useRef(onNoData);
  onErrorRef.current = onError;
  onNoDataRef.current = onNoData;

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    getCompanyDirectorData(ric, token, sortBy, orderBy)
      .then((res: CompanyDirectorResponse) => {
        if (!cancelled) {
          const seen = new Set<number>();
          const unique = (res?.data ?? []).filter((d) => {
            if (seen.has(d.Officer_ID)) return false;
            seen.add(d.Officer_ID);
            return true;
          });
          setData(unique);
          if (unique.length === 0) {
            onNoDataRef.current?.({
              type: WidgetMessageType.NoData,
              source: 'CompanyDirector',
              data: null,
            });
          }
        }
      })
      .catch((err: Error & { code?: string | number }) => {
        if (!cancelled) {
          setError(err.message);
          onErrorRef.current?.({
            message: err?.message,
            type: `${err?.name}_${err?.code}`,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ric, token, sortBy, orderBy]);

  return { data, isLoading, error };
};

export default useCompanyDirector;
